/**
 * QuestionTemplates.js
 * 
 * Defines templates for all question types (questionnaire and challenge).
 * Templates ensure consistent structure with all required fields.
 */

/**
 * Question Types Configuration by Main Category
 * Each main category explicitly defines which question types are available
 */
export const QUESTION_TYPES_BY_MAIN_CATEGORY = {
  // Technical categories - ALL question types including code
  'Software Development / Engineering': [
    'code-write',
    'code-completion',
    'code-explanation',
    'code-refactoring',
    'find-the-bug',
    'security-task',
    'performance-optimization',
    'multiple-answer',
    'single-answer',
    'true-false',
    'long-text',
    'short-text',
    "audio-answer",
    'video-answer'
  ],
  
  'Data & Analytics': [
    'code-write',
    'code-completion',
    'code-explanation',
    'code-refactoring',
    'find-the-bug',
    'security-task',
    'performance-optimization',
    'multiple-answer',
    'single-answer',
    'true-false',
    'long-text',
    'short-text',
    "audio-answer",
    'video-answer'
  ],
  
  'IT & Infrastructure': [
    'code-write',
    'code-completion',
    'code-explanation',
    'code-refactoring',
    'find-the-bug',
    'security-task',
    'performance-optimization',
    'multiple-answer',
    'single-answer',
    'true-false',
    'long-text',
    'short-text',
    "audio-answer",
    'video-answer'
  ],
  
  // Non-technical categories - NO code questions
  'Product Management & Strategy': [
    'multiple-answer',
    'single-answer',
    'true-false',
    'long-text',
    'short-text',
    "audio-answer",
    'video-answer'
  ],
  'Sales & Business Development': [
    'multiple-answer',
    'single-answer',
    'true-false',
    'long-text',
    'short-text',
    "audio-answer",
    'video-answer'
  ],
  'Customer Success & Support': [
    'multiple-answer',
    'single-answer',
    'true-false',
    'long-text',
    'short-text',
    "audio-answer",
    'video-answer'
  ],    
  'Human Resources & Talent Acquisition': [
    'multiple-answer',
    'single-answer',
    'true-false',
    'long-text',
    'short-text',
    "audio-answer",
    'video-answer'
  ]
};

/**
 * Get all available main categories
 * @returns {string[]} Array of main category names
 */
export const getAllMainCategories = () => {
  return Object.keys(QUESTION_TYPES_BY_MAIN_CATEGORY);
};

/**
 * Get available question types for a specific main category
 * @param {string} mainCategory - The main category name
 * @returns {string[]} Array of available question type codes
 */
export const getQuestionTypesForMainCategory = (mainCategory) => {
  return QUESTION_TYPES_BY_MAIN_CATEGORY[mainCategory] || [];
};

/**
 * Question type display labels
 */
export const QUESTION_TYPE_LABELS = {
  'code-write': 'Code Write',
  'code-completion': 'Code Completion',
  'code-explanation': 'Code Explanation',
  'code-refactoring': 'Code Refactoring',
  'find-the-bug': 'Find the Bug',
  'security-task': 'Security Task',
  'performance-optimization': 'Performance Optimization',
  'multiple-answer': 'Multiple Choice',
  'single-answer': 'Single Choice',
  'true-false': 'True/False',
  'long-text': 'Long Text',
  'short-text': 'Short Text',
  'audio-answer': 'Audio',
  'video-answer': 'Video',
};

/**
 * Get display label for a question type
 * @param {string} type - Question type code
 * @returns {string} Display label
 */
export const getQuestionTypeLabel = (type) => {
  return QUESTION_TYPE_LABELS[type] || type;
};

/**
 * Questionnaire Question Templates
 */
export const QUESTIONNAIRE_QUESTION_TEMPLATES = {
  'multiple-answer': {
    id: '',
    uuid: '',
    type: 'multiple-answer',
    questionText: '',
    inRelationTo: [],
    options: [],
    userInput: 'checkboxes',
    userInputOther: 'textinput',
    timeToSolve: '5',
  },
  'single-answer': {
    id: '',
    uuid: '',
    type: 'single-answer',
    questionText: '',
    inRelationTo: [],
    options: [],
    userInput: 'radio',
    userInputOther: 'textinput',
    timeToSolve: '5',
  },
  'long-text': {
    id: '',
    uuid: '',
    type: 'long-text',
    questionText: '',
    inRelationTo: [],
    maxLength: '2500',
    userInput: 'textarea',
    placeholder: 'Enter your answer here...',
    timeToSolve: '10',
  },
  'short-text': {
    id: '',
    uuid: '',
    type: 'short-text',
    questionText: '',
    inRelationTo: [],
    maxLength: '500',
    userInput: 'textarea',
    placeholder: 'Enter your answer here...',
    timeToSolve: '5',
  },
  'audio-answer': {
    id: '',
    uuid: '',
    type: 'audio-answer',
    questionText: '',
    inRelationTo: [],
    max_duration: '240',
    userInput: 'audio',
    timeToSolve: '6',
  },
  'video-answer': {
    id: '',
    uuid: '',
    type: 'video-answer',
    questionText: '',
    inRelationTo: [],
    max_duration: '240',
    userInput: 'video',
    timeToSolve: '6',
  },
};

/**
 * Challenge Question Templates
 */
export const CHALLENGE_QUESTION_TEMPLATES = {
  'code-write': {
    id: '',
    uuid: '',
    type: 'code-write',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    userInput: 'codebox',
    placeholder: 'Write your code solution here...',
    timeToSolve: '15',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'code-completion': {
    id: '',
    uuid: '',
    type: 'code-completion',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    codeSnippet: '',
    userInput: 'codebox',
    placeholder: 'Enter the missing code to complete the snippet...',
    timeToSolve: '10',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'code-explanation': {
    id: '',
    uuid: '',
    type: 'code-explanation',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    codeSnippet: '',
    maxLength: '1000',
    userInput: 'textarea',
    placeholder: 'Explain the code here...',
    timeToSolve: '10',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'code-refactoring': {
    id: '',
    uuid: '',
    type: 'code-refactoring',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    codeSnippet: '',
    userInput: 'codebox',
    placeholder: 'Enter your refactored code here...',
    timeToSolve: '15',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'find-the-bug': {
    id: '',
    uuid: '',
    type: 'find-the-bug',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    codeSnippet: '',
    maxLength: '1000',
    userInput: 'textarea',
    placeholder: 'Explain the bug and how to fix it...',
    timeToSolve: '10',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'security-task': {
    id: '',
    uuid: '',
    type: 'security-task',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    codeSnippet: '',
    maxLength: '1000',
    userInput: 'textarea',
    placeholder: 'Explain the security concern and suggest fixes...',
    timeToSolve: '15',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'performance-optimization': {
    id: '',
    uuid: '',
    type: 'performance-optimization',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    codeSnippet: '',
    userInput: 'textarea',
    placeholder: 'Suggest performance optimization improvements...',
    timeToSolve: '15',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'multiple-answer': {
    id: '',
    uuid: '',
    type: 'multiple-answer',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    options: [],
    userInput: 'checkboxes',
    userInputOther: 'textinput',
    timeToSolve: '5',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'single-answer': {
    id: '',
    uuid: '',
    type: 'single-answer',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    options: [],
    userInput: 'radio',
    userInputOther: 'textinput',
    timeToSolve: '5',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'true-false': {
    id: '',
    uuid: '',
    type: 'true-false',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    statement: '',
    options: ['True', 'False'],
    userInput: 'radio',
    timeToSolve: '3',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'long-text': {
    id: '',
    uuid: '',
    type: 'long-text',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    maxLength: '2500',
    userInput: 'textinput',
    placeholder: 'Enter your detailed answer here...',
    timeToSolve: '10',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'short-text': {
    id: '',
    uuid: '',
    type: 'short-text',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    maxLength: '1000',
    userInput: 'textinput',
    placeholder: 'Enter your brief answer here...',
    timeToSolve: '6',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'audio-answer': {
    id: '',
    uuid: '',
    type: 'audio-answer',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    max_duration: '240',
    userInput: 'audio',
    timeToSolve: '6',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },
  'video-answer': {
    id: '',
    uuid: '',
    type: 'video-answer',
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    max_duration: '240',
    userInput: 'video',
    timeToSolve: '6',
    breadth: '',
    completed: false,
    followup_questions_and_answers: [],
    max_followups: 2,
    round_index: 0,
  },  
};

/**
 * Get a deep copy of a question template
 * 
 * @param {string} type - Question type
 * @param {string} category - 'questionnaire' or 'challenge'
 * @returns {Object} Template copy
 */
export const getQuestionTemplate = (type, category = 'questionnaire') => {
  const templates = category === 'challenge' 
    ? CHALLENGE_QUESTION_TEMPLATES 
    : QUESTIONNAIRE_QUESTION_TEMPLATES;
  
  const template = templates[type];
  if (!template) {
    throw new Error(`Unknown ${category} question type: ${type}`);
  }
  
  return JSON.parse(JSON.stringify(template));
};

/**
 * Create a new question from template with custom values
 * 
 * @param {string} type - Question type
 * @param {string} category - 'questionnaire' or 'challenge'
 * @param {Object} overrides - Custom values to override defaults
 * @returns {Object} New question with all required fields
 */
export const createQuestionFromTemplate = (type, category = 'questionnaire', overrides = {}) => {
  const template = getQuestionTemplate(type, category);
  return { ...template, ...overrides };
};

/**
 * Check if a question type requires a code snippet field
 * 
 * @param {string} type - Question type
 * @returns {boolean} True if the type requires codeSnippet
 */
export const questionTypeRequiresCodeSnippet = (type) => {
  const template = CHALLENGE_QUESTION_TEMPLATES[type];
  return template && 'codeSnippet' in template;
};

/**
 * Check if a question type requires an options array
 * 
 * @param {string} type - Question type
 * @param {string} category - 'questionnaire' or 'challenge'
 * @returns {boolean} True if the type requires options
 */
export const questionTypeRequiresOptions = (type, category = 'questionnaire') => {
  const templates = category === 'challenge' 
    ? CHALLENGE_QUESTION_TEMPLATES 
    : QUESTIONNAIRE_QUESTION_TEMPLATES;
  const template = templates[type];
  return template && 'options' in template;
};

/**
 * Check if a question type requires a statement field (true-false)
 * 
 * @param {string} type - Question type
 * @returns {boolean} True if the type requires statement
 */
export const questionTypeRequiresStatement = (type) => {
  const template = CHALLENGE_QUESTION_TEMPLATES[type];
  return template && 'statement' in template;
};

/**
 * Check if a question type requires a maxLength field
 * 
 * @param {string} type - Question type
 * @param {string} category - 'questionnaire' or 'challenge'
 * @returns {boolean} True if the type requires maxLength
 */
export const questionTypeRequiresMaxLength = (type, category = 'questionnaire') => {
  const templates = category === 'challenge' 
    ? CHALLENGE_QUESTION_TEMPLATES 
    : QUESTIONNAIRE_QUESTION_TEMPLATES;
  const template = templates[type];
  return template && 'maxLength' in template;
};
